import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Skeleton,
    Chip,
    useTheme,
    useMediaQuery,
    Fab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { customersAPI } from '../api/api';
import { formatCurrency } from '../config';
import { useAuth } from '../contexts/AuthContext';

const Customers = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user } = useAuth();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        gst_number: '',
        credit_limit: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');

    useEffect(() => {
        fetchCustomers();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const response = await customersAPI.getAll();
            if (response.data.success) {
                setCustomers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (customer = null) => {
        if (customer) {
            setEditingCustomer(customer);
            setFormData({
                name: customer.name,
                contact_person: customer.contact_person || '',
                email: customer.email || '',
                phone: customer.phone || '',
                address: customer.address || '',
                gst_number: customer.gst_number || '',
                credit_limit: customer.credit_limit || ''
            });
        } else {
            setEditingCustomer(null);
            setFormData({
                name: '',
                contact_person: '',
                email: '',
                phone: '',
                address: '',
                gst_number: '',
                credit_limit: ''
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCustomer(null);
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Customer name is required');
            return;
        }

        try {
            const payload = {
                ...formData,
                credit_limit: parseFloat(formData.credit_limit) || 0
            };

            if (editingCustomer) {
                await customersAPI.update(editingCustomer.id, payload);
                toast.success('Customer updated');
            } else {
                await customersAPI.create(payload);
                toast.success('Customer added');
            }
            handleCloseDialog();
            fetchCustomers();
        } catch (error) {
            toast.error('Failed to save customer');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Customer?',
            text: 'This may deactivate the customer if they have invoices.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                await customersAPI.delete(id);
                toast.success('Customer deleted/deactivated');
                fetchCustomers();
            } catch (error) {
                toast.error('Failed to delete customer');
            }
        }
    };

    // Filter customers
    const filteredCustomers = customers
        .filter(c =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.contact_person && c.contact_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (c.phone && c.phone.includes(searchTerm))
        )
        .sort((a, b) => {
            const isAsc = order === 'asc';
            const valueA = a[orderBy] || '';
            const valueB = b[orderBy] || '';

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return isAsc ? valueA - valueB : valueB - valueA;
            }

            const strA = String(valueA).toLowerCase();
            const strB = String(valueB).toLowerCase();

            if (strA < strB) return isAsc ? -1 : 1;
            if (strA > strB) return isAsc ? 1 : -1;
            return 0;
        });

    // Mobile Card View
    const CustomerCard = ({ customer }) => (
        <Card sx={{ mb: 2 }} className="hover-lift">
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                            {customer.name}
                        </Typography>
                        {customer.contact_person && (
                            <Typography variant="body2" color="text.secondary">
                                {customer.contact_person}
                            </Typography>
                        )}
                    </Box>
                    {user && user.role !== 'VIEWER' && (
                        <Box>
                            <IconButton size="small" onClick={() => handleOpenDialog(customer)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(customer.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    {customer.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">{customer.phone}</Typography>
                        </Box>
                    )}
                    {customer.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{customer.email}</Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
                        <Typography variant="body2">{formatCurrency(customer.total_invoiced || 0)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            color={(customer.outstanding_balance || 0) > 0 ? 'error.main' : 'text.primary'}
                        >
                            {formatCurrency(customer.outstanding_balance || 0)}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                    Customers
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, maxWidth: { md: 400 }, ml: { md: 4 } }}>
                    <TextField
                        fullWidth
                        placeholder="Search customers..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon size="small" color="action" sx={{ mr: 1 }} />
                        }}
                    />
                </Box>
                {!isMobile && user && user.role !== 'VIEWER' && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add Customer
                    </Button>
                )}
            </Box>
            {/* Summary Cards */}
            {!loading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Card className="kpi-card info">
                            <CardContent>
                                <Typography className="kpi-label">Total Customers</Typography>
                                <Typography className="kpi-value">{customers.length}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card className="kpi-card error">
                            <CardContent>
                                <Typography className="kpi-label">Total Outstanding</Typography>
                                <Typography className="kpi-value">
                                    {formatCurrency(customers.reduce((sum, c) => sum + (c.outstanding_balance || 0), 0))}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card className="kpi-card success">
                            <CardContent>
                                <Typography className="kpi-label">Active Customers</Typography>
                                <Typography className="kpi-value">
                                    {customers.filter(c => (c.total_invoiced || 0) > 0).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card className="kpi-card warning">
                            <CardContent>
                                <Typography className="kpi-label">Credit Limit (Avg)</Typography>
                                <Typography className="kpi-value">
                                    {formatCurrency(customers.length > 0 ? (customers.reduce((sum, c) => sum + (c.credit_limit || 0), 0) / customers.length) : 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Content */}
            {loading ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 1 }} />)
            ) : isMobile ? (
                <Box>
                    {filteredCustomers.map((customer) => (
                        <CustomerCard key={customer.id} customer={customer} />
                    ))}
                    {customers.length === 0 && (
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No customers yet</Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            ) : (
                <Card className="next-gen-table">
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' ? order : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            Customer
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'contact_person'}
                                            direction={orderBy === 'contact_person' ? order : 'asc'}
                                            onClick={() => handleRequestSort('contact_person')}
                                        >
                                            Contact
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'gst_number'}
                                            direction={orderBy === 'gst_number' ? order : 'asc'}
                                            onClick={() => handleRequestSort('gst_number')}
                                        >
                                            GST Number
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'total_invoiced'}
                                            direction={orderBy === 'total_invoiced' ? order : 'asc'}
                                            onClick={() => handleRequestSort('total_invoiced')}
                                        >
                                            Total Invoiced
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'total_paid'}
                                            direction={orderBy === 'total_paid' ? order : 'asc'}
                                            onClick={() => handleRequestSort('total_paid')}
                                        >
                                            Paid
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'outstanding_balance'}
                                            direction={orderBy === 'outstanding_balance' ? order : 'asc'}
                                            onClick={() => handleRequestSort('outstanding_balance')}
                                        >
                                            Outstanding
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCustomers.map((customer) => (
                                    <TableRow
                                        key={customer.id}
                                        hover
                                        onClick={() => handleOpenDialog(customer)}
                                        className="MuiTableRow-hover"
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography fontWeight={500}>{customer.name}</Typography>
                                                    {customer.contact_person && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {customer.contact_person}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {user && user.role !== 'VIEWER' && (
                                                    <Box className="row-actions">
                                                        <IconButton
                                                            className="action-icon-btn edit"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleOpenDialog(customer); }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            className="action-icon-btn delete"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(customer.id); }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {customer.phone && <Typography variant="body2">{customer.phone}</Typography>}
                                            {customer.email && (
                                                <Typography variant="caption" color="text.secondary">{customer.email}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{customer.gst_number || '-'}</TableCell>
                                        <TableCell align="right">{formatCurrency(customer.total_invoiced || 0)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                            {formatCurrency(customer.total_paid || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{
                                            color: (customer.outstanding_balance || 0) > 0 ? 'error.main' : 'text.primary',
                                            fontWeight: 600
                                        }}>
                                            {formatCurrency(customer.outstanding_balance || 0)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={customer.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={customer.is_active ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {customers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No customers yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Mobile FAB */}
            {isMobile && user && user.role !== 'VIEWER' && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 72, right: 16 }}
                    onClick={() => handleOpenDialog()}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Customer Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Contact Person"
                                value={formData.contact_person}
                                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="GST Number"
                                value={formData.gst_number}
                                onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Credit Limit (₹)"
                                type="number"
                                value={formData.credit_limit}
                                onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editingCustomer ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Customers;
